// lib/generation-postprocess-client.ts
import { computeGenerationPostprocess } from '#lib/generation-postprocess';
import { copyPixels, decodeWorkerPixels, runWithWorkerFallback, sendWorkerRequest, toImageData, type WirePixels } from '#lib/worker-rpc';

type GenerationPostprocessJob = Parameters<typeof computeGenerationPostprocess>[1];
type LayerFinishJob = Extract<GenerationPostprocessJob, { kind: 'layerFinish' }>;
type HighResMaskingInput = NonNullable<LayerFinishJob['highRes']>;

type WireJob =
  | Exclude<GenerationPostprocessJob, LayerFinishJob>
  | (Omit<LayerFinishJob, 'highRes'> & {
    highRes?: Omit<HighResMaskingInput, 'layers'> & {
      layers: Array<Omit<HighResMaskingInput['layers'][number], 'imageData'> & { pixels: WirePixels }>;
    };
  });

interface GenerationPostprocessWorkerRequest {
  messageId: string;
  target: WirePixels;
  job: WireJob;
}

type GenerationPostprocessWorkerResponse =
  | { messageId: string; error: string }
  | { messageId: string; result: WirePixels };

// The slowest job measured on the main thread (preserve-high-res at 8192², 4.4 s) extrapolates to
// under 20 s at the 16K upscale ceiling: 60 s is headroom for slow machines, yet bounds a worker
// that never loaded, which would otherwise hang the generation and lose a paid result.
// It measures worker silence, not job duration: a queued job must not expire behind a live worker's backlog.
const GENERATION_POSTPROCESS_WORKER_TIMEOUT_MS = 60_000;

const encodeJob = (job: GenerationPostprocessJob, transfer: ArrayBuffer[]): WireJob => {
  if (job.kind === 'blurAlpha') return job;
  const { highRes, ...rest } = job;
  return {
    ...rest,
    highRes: highRes && {
      ...highRes,
      layers: highRes.layers.map(({ imageData, ...layer }) => {
        const pixels = copyPixels(imageData);
        transfer.push(pixels.buffer);
        return { ...layer, pixels };
      }),
    },
  };
};

const decodeJob = (job: WireJob): GenerationPostprocessJob => {
  if (job.kind === 'blurAlpha') return job;
  const { highRes, ...rest } = job;
  return {
    ...rest,
    highRes: highRes && {
      ...highRes,
      layers: highRes.layers.map(({ pixels, ...layer }) => ({ ...layer, imageData: toImageData(pixels) })),
    },
  };
};

export const handleGenerationPostprocessWorkerRequest = (
  request: GenerationPostprocessWorkerRequest,
): { response: GenerationPostprocessWorkerResponse; transfer: Transferable[] } => {
  try {
    const result = computeGenerationPostprocess(toImageData(request.target), decodeJob(request.job));
    const buffer = result.data.buffer as ArrayBuffer;
    return {
      response: { messageId: request.messageId, result: { buffer, width: result.width, height: result.height } },
      transfer: [buffer],
    };
  } catch (error) {
    return {
      response: { messageId: request.messageId, error: error instanceof Error ? error.message : 'Unknown generation postprocess worker error.' },
      transfer: [],
    };
  }
};

/** Every source is copied, never transferred: the originals stay the fallback input. */
export const runGenerationPostprocessInWorker = (
  worker: Worker,
  target: ImageData,
  job: GenerationPostprocessJob,
  timeoutMs: number = GENERATION_POSTPROCESS_WORKER_TIMEOUT_MS,
): Promise<ImageData> => {
  const targetPixels = copyPixels(target);
  const transfer: ArrayBuffer[] = [targetPixels.buffer];
  const payload: Omit<GenerationPostprocessWorkerRequest, 'messageId'> = { target: targetPixels, job: encodeJob(job, transfer) };
  return sendWorkerRequest<typeof payload, WirePixels>(
    worker,
    payload,
    transfer,
    { label: 'Generation postprocess', timeoutMs, errorEventMarksUnavailable: true },
  ).then((result) => decodeWorkerPixels('Generation postprocess', result));
};

export const runGenerationPostprocessWithFallback = (
  workerRef: { current: Worker | null },
  target: ImageData,
  job: GenerationPostprocessJob,
  timeoutMs: number = GENERATION_POSTPROCESS_WORKER_TIMEOUT_MS,
) => runWithWorkerFallback(
  workerRef,
  (worker) => runGenerationPostprocessInWorker(worker, target, job, timeoutMs),
  () => computeGenerationPostprocess(target, job),
  '[GenerationPostprocess] Worker postprocess failed, falling back to main thread.',
);
